import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useOrganizerAuth } from "../contexts/organizer-auth-context";
import { AppNavLink, useAppNavigate } from "../lib/route-primitives";
import {
  Calendar, MapPin, Loader2, Plus, Archive, ArchiveRestore,
  Heart, X, CheckCircle2, XCircle, ArrowRight, ChevronDown, Package, ExternalLink,
} from "lucide-react";
import { organizerPackagesApi, organizerWeddingsApi } from "../lib/api";
import { NEW_WEDDING_PACKAGE_PARAM, paths } from "../lib/paths";
import {
  PLAN_LABELS, SLUG_MIN_LENGTH, SLUG_MAX_LENGTH,
  type AccountPackage, type AccountPackagesSummary, type PendingPackage, type WeddingPackageInfo,
} from "@wedding/shared";
import { OrganizerPageHeader } from "../components/organizer-page-header";

const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

const SLUG_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
function isSlugValid(slug: string): boolean {
  return slug.length >= SLUG_MIN_LENGTH && slug.length <= SLUG_MAX_LENGTH && SLUG_REGEX.test(slug);
}

// Unicode letter + allowed name chars (covers Turkish ş,ğ,ü,ö,ı,ç etc.)
const NAME_REGEX = /^[\p{L}\s'\-]+$/u;
// Location: letters, digits, common address punctuation
const LOCATION_REGEX = /^[\p{L}\p{N}\s,.'"\-\/\(\)&#]+$/u;
import { cn } from "../lib/utils";

// ── Helpers ────────────────────────────────────────────────────────────────

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function extractMonogram(title: string): string {
  const parts = title.split(/\s*[&+]\s*|\s+ve\s+|\s+and\s+/i).map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0].toUpperCase()} & ${parts[parts.length - 1][0].toUpperCase()}`;
  }
  return title.slice(0, 2).toUpperCase();
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/ş/g, "s").replace(/ğ/g, "g").replace(/ü/g, "u")
    .replace(/ö/g, "o").replace(/ı/g, "i").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

// ── Types ──────────────────────────────────────────────────────────────────

interface WeddingSummary {
  slug: string;
  title: string;
  date_time: string | null;
  location: string | null;
  /** Düğünün paketi (sipariş ya da hesap planı). */
  package?: WeddingPackageInfo;
}

type ListData = {
  total?: number;
  maxWeddings?: number;
  freeUsed?: number;
  planLabel?: string;
  planGuests?: number;
  pendingPackages?: PendingPackage[];
  packages?: AccountPackagesSummary;
};

const NO_LIMITS = { guests: 0, media: 0, storageBytes: 0, smsSegments: 0, reminderSends: 0 };

/** Paket özeti yoksa (eski worker, dağıtım arası) eski alanlardan türetilir: deneme hakkı + bekleyen paketler. */
function packagesFrom(d: ListData): AccountPackagesSummary {
  if (d.packages) return d.packages;
  const freeLeft = Math.max(0, (d.maxWeddings ?? 0) - (d.freeUsed ?? d.total ?? 0));
  const available: AccountPackage[] = (d.pendingPackages ?? []).map((p) => ({
    id: p.id, source: "order", label: p.label ?? PLAN_LABELS[p.plan], number: p.number,
    limits: { ...NO_LIMITS, guests: p.guests ?? 0 },
    status: "available", wedding: null, amountTry: null, createdAt: p.paidAt ?? "",
  }));
  if (freeLeft > 0) {
    available.unshift({
      id: "trial", source: "free", label: d.planLabel ?? "Free", number: null,
      limits: { ...NO_LIMITS, guests: d.planGuests ?? 0 },
      status: "available", wedding: null, amountTry: null, createdAt: "",
    });
  }
  return { ready: false, available, inUse: [], pending: [], ended: [] };
}

const EMPTY_PACKAGES: AccountPackagesSummary = { ready: false, available: [], inUse: [], pending: [], ended: [] };

const APPLY_ERRORS: Record<string, string> = {
  package_unavailable: "wedding.packageUnavailable",
  already_paid: "wedding.alreadyPaid",
  wedding_has_package: "wedding.weddingHasPackage",
  consent_required: "wedding.consentRequired",
};
const CREATE_ERRORS: Record<string, string> = {
  package_required: "wedding.packageRequired",
  consent_required: "wedding.consentRequired",
  package_unavailable: "wedding.packageUnavailable",
};

// ── Sub-components ─────────────────────────────────────────────────────────

function MonogramPlaceholder({ title }: { title: string }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#262220]">
      <div style={{ filter: "blur(50px)" }} className="absolute inset-0">
        <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-primary/40" />
        <div className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full bg-primary/20" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-serif text-5xl text-primary/60 select-none">
          {extractMonogram(title)}
        </span>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export function MyWeddingsPage() {
  const { t, i18n } = useTranslation();
  const { organizer } = useOrganizerAuth();
  const navigate = useAppNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // ── Data state ──────────────────────────────────────────────────────────
  const [weddings, setWeddings] = useState<WeddingSummary[]>([]);
  const [archivedWeddings, setArchivedWeddings] = useState<WeddingSummary[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [totalWeddings, setTotalWeddings] = useState(0);
  /** Hesaba özel plan (eski Pro/Max hesap, paket modeline henüz taşınmamış) — sunucudan. */
  const [accountWide, setAccountWide] = useState<{ slug: string; label: string } | null>(null);
  /**
   * Paket modeli (2026-10-16): her düğün bir pakete bağlıdır. Kullanılabilir paketler (hesabın
   * Free paketi + ödenmiş, tanımlanmamış siparişler) yeni düğün açar ya da mevcut düğünü yükseltir.
   */
  const [packages, setPackages] = useState<AccountPackagesSummary>(EMPTY_PACKAGES);
  /** Yeni düğünün paketi — "" = seçilmedi (ön seçim yok; yalnız Paketlerim'den `?package=` ile). */
  const [usePackage, setUsePackage] = useState("");
  /** Ücretli paket: hizmet hemen başlar, cayma hakkı biter (m.15) — kutu işaretsiz başlar. */
  const [startConsent, setStartConsent] = useState(false);
  /** Paket bandında seçilen mevcut düğün + onay (paket id → slug / onay). */
  const [applyTarget, setApplyTarget] = useState<Record<string, string>>({});
  const [applyConsent, setApplyConsent] = useState<Record<string, boolean>>({});
  const [applying, setApplying] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Create modal state ──────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ slug: "", brideName: "", groomName: "", dateTime: "", location: "" });
  const [slugManual, setSlugManual] = useState(false);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [fieldErrors, setFieldErrors] = useState<{ brideName?: string; groomName?: string; location?: string }>({});
  const [isCreating, setIsCreating] = useState(false);
  const [serverError, setServerError] = useState("");

  // ── Toast state ─────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  }, []);

  // Kullanılabilir paket → yeni düğün açılır. Ödeme bekleyen sipariş henüz paket değil (seçilemez).
  const canCreate = packages.available.length > 0;
  const pkgText = (p: AccountPackage) =>
    p.source === "free"
      ? p.id === "trial" && accountWide
        ? t("wedding.pkgAccountWide", { plan: p.label, n: p.limits.guests })
        : t("wedding.pkgFree", { plan: p.label, n: p.limits.guests })
      : p.source === "legacy"
        ? t("wedding.pkgLegacy", { plan: p.label, n: p.limits.guests })
        : t("wedding.pkgWithGuests", { plan: p.label, n: p.limits.guests });
  const cardLabel = (pkg: WeddingPackageInfo) => {
    const o = { plan: pkg.label, n: pkg.limits.guests };
    switch (pkg.source) {
      case "order": return t("wedding.pkgWithGuests", o);
      case "account": return t("wedding.pkgAccountWide", o);
      case "free": return t("wedding.pkgFree", o);
      case "legacy": return t("wedding.pkgLegacy", o);
      case "suspended": return t("wedding.pkgSuspended");
      default: return t("wedding.pkgTrial", o);
    }
  };
  /** "1 Free · 2 Pro" */
  const walletList = Object.entries(
    packages.available.reduce<Record<string, number>>((acc, p) => ({ ...acc, [p.label]: (acc[p.label] ?? 0) + 1 }), {}),
  ).map(([label, n]) => `${n} ${label}`).join(" · ");
  const selectedPackage = packages.available.find((p) => p.id === usePackage) ?? null;
  const needsConsent = selectedPackage?.source === "order";
  /** Bantta paketin tanımlanabileceği düğünler: ücretli → ücretli paketi olmayanlar; Free → yalnız askıdakiler. */
  const assignTargets = (p: AccountPackage) =>
    p.source === "order"
      ? weddings.filter((w) => w.package?.source !== "order")
      : p.source === "free" && p.id !== "trial"
        ? weddings.filter((w) => w.package?.source === "suspended")
        : [];
  const assignable = packages.available.filter((p) => assignTargets(p).length > 0);

  // ── Load weddings ───────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    const res = await organizerWeddingsApi.list();
    if (res.ok && res.data) {
      setWeddings(res.data.weddings as WeddingSummary[]);
      setArchivedWeddings((res.data.archivedWeddings ?? []) as WeddingSummary[]);
      setTotalWeddings(res.data.total ?? res.data.weddings.length);
      setAccountWide(res.data.accountWide ?? null);
      setPackages(packagesFrom(res.data));
    }
  }, []);
  useEffect(() => {
    refresh()
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [refresh]);

  const openCreate = (packageId = "") => {
    setUsePackage(packageId);
    setStartConsent(false);
    setShowModal(true);
  };

  // Paketlerim → "Bu paketle düğün aç": pencere o paket seçili açılır (yalnız burada ön seçim var).
  const wanted = searchParams.get(NEW_WEDDING_PACKAGE_PARAM);
  useEffect(() => {
    if (isLoading || !wanted) return;
    if (packages.available.some((p) => p.id === wanted)) openCreate(wanted);
    else showToast(t("wedding.packageUnavailable"), false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(NEW_WEDDING_PACKAGE_PARAM);
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, wanted]);

  // ── Kullanılabilir paketi MEVCUT bir düğüne tanımla (geri alınamaz — ücretli pakette onay kutusu) ──
  const handleApply = async (pkg: AccountPackage) => {
    const slug = applyTarget[pkg.id] ?? "";
    const target = weddings.find((w) => w.slug === slug);
    if (!target) return;
    const paid = pkg.source === "order";
    if (paid && !applyConsent[pkg.id]) {
      showToast(t("wedding.consentRequired"), false);
      return;
    }
    setApplying(pkg.id);
    try {
      await organizerPackagesApi.assign(pkg.id, slug, paid);
      await refresh();
      setApplyTarget((prev) => ({ ...prev, [pkg.id]: "" }));
      setApplyConsent((prev) => ({ ...prev, [pkg.id]: false }));
      showToast(t("wedding.applied", { title: target.title }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      showToast(APPLY_ERRORS[msg] ? t(APPLY_ERRORS[msg]) : t("wedding.applyFailed"), false);
      if (msg === "package_unavailable" || msg === "already_paid") void refresh();
    } finally {
      setApplying(null);
    }
  };

  // ── Field validation ────────────────────────────────────────────────────
  function validateName(value: string): string | undefined {
    if (!value.trim()) return t("wedding.enterBride"); // caller passes right key
    if (!NAME_REGEX.test(value.trim())) return t("wedding.nameLettersOnly");
  }
  function validateLocation(value: string): string | undefined {
    if (!value.trim()) return t("wedding.enterLocation");
    if (!LOCATION_REGEX.test(value.trim())) return t("wedding.locationInvalid");
  }
  function setFieldError(field: keyof typeof fieldErrors, value: string) {
    setFieldErrors((prev) => ({ ...prev, [field]: value }));
  }
  function clearFieldError(field: keyof typeof fieldErrors) {
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  // ── Auto-slug from bride+groom names (only updates text, no request) ────
  const updateAutoSlug = useCallback((brideName: string, groomName: string) => {
    if (slugManual) return;
    const parts = [brideName, groomName].map(slugify).filter(Boolean);
    setFormData((prev) => ({ ...prev, slug: parts.join("-") }));
    setSlugStatus("idle");
  }, [slugManual]);

  // ── Slug check: validates format first, then DB availability ─────────
  async function checkSlug(slug: string) {
    if (!slug) { setSlugStatus("idle"); return; }
    if (!isSlugValid(slug)) { setSlugStatus("invalid"); return; }
    setSlugStatus("checking");
    try {
      const res = await organizerWeddingsApi.checkSlug(slug);
      setSlugStatus(res.data?.available ? "available" : "taken");
    } catch {
      setSlugStatus("idle");
    }
  }

  // ── ESC closes modal ─────────────────────────────────────────────────
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setFormData({ slug: "", brideName: "", groomName: "", dateTime: "", location: "" });
        setSlugManual(false);
        setSlugStatus("idle");
        setFieldErrors({});
        setServerError("");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  // ── Create handler ──────────────────────────────────────────────────────
  const handleCreate = async () => {
    // Validate all fields upfront
    const brideErr = validateName(formData.brideName) ?? (formData.brideName ? undefined : t("wedding.enterBride"));
    const groomErr = validateName(formData.groomName) ?? (formData.groomName ? undefined : t("wedding.enterGroom"));
    const locationErr = validateLocation(formData.location);
    setFieldErrors({ brideName: brideErr, groomName: groomErr, location: locationErr });
    if (brideErr || groomErr || locationErr) return;

    if (!isSlugValid(formData.slug)) { setSlugStatus("invalid"); return; }
    if (slugStatus === "taken" || slugStatus === "invalid") return;
    if (!formData.dateTime) { setServerError(t("wedding.enterDate")); return; }
    if (!selectedPackage) { setServerError(t("wedding.packageRequired")); return; }
    if (needsConsent && !startConsent) { setServerError(t("wedding.consentRequired")); return; }

    // Status idle means user never focused slug field — check inline now
    if (slugStatus === "idle") {
      setSlugStatus("checking");
      try {
        const res = await organizerWeddingsApi.checkSlug(formData.slug);
        const available = res.data?.available ?? false;
        setSlugStatus(available ? "available" : "taken");
        if (!available) return;
      } catch {
        setSlugStatus("idle");
        return;
      }
    }

    setIsCreating(true);
    setServerError("");
    try {
      const title = `${formData.brideName} & ${formData.groomName}`;
      const created = await organizerWeddingsApi.create({
        slug: formData.slug,
        title,
        dateTime: formData.dateTime,
        location: formData.location.trim(),
        packageId: selectedPackage.id,
        ...(needsConsent ? { startConsent: true } : {}),
      });
      await refresh();
      const createdSlugValue = formData.slug;
      resetForm();
      showToast(needsConsent || created.data?.packageOrder ? t("wedding.createdWithPackage") : t("wedding.created"));
      setShowModal(false);
      void navigate(paths.wedding.settings(createdSlugValue));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "";
      setServerError(
        msg === "free_slots_full"
          ? t("wedding.freeSlotsFull")
          : CREATE_ERRORS[msg]
            ? t(CREATE_ERRORS[msg])
            : msg || t("wedding.createFailed"),
      );
      if (msg === "package_unavailable" || msg === "free_slots_full") {
        setUsePackage("");
        void refresh();
      }
    } finally {
      setIsCreating(false);
    }
  };

  // ── Archive handler ─────────────────────────────────────────────────────
  const handleArchive = async (slug: string) => {
    if (!confirm(t("wedding.archiveConfirm", { slug }))) return;
    try {
      await organizerWeddingsApi.archive(slug);
      const w = weddings.find(x => x.slug === slug);
      setWeddings(prev => prev.filter(x => x.slug !== slug));
      if (w) setArchivedWeddings(prev => [w, ...prev]);
      showToast(t("wedding.archived"));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("wedding.archiveFailed"), false);
    }
  };

  // ── Unarchive handler ────────────────────────────────────────────────────
  const handleUnarchive = async (slug: string) => {
    try {
      await organizerWeddingsApi.unarchive(slug);
      const w = archivedWeddings.find(x => x.slug === slug);
      setArchivedWeddings(prev => prev.filter(x => x.slug !== slug));
      if (w) setWeddings(prev => [w, ...prev]);
      showToast(t("wedding.unarchived"));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("wedding.unarchiveFailed"), false);
    }
  };

  // ── Reset / close modal ──────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ slug: "", brideName: "", groomName: "", dateTime: "", location: "" });
    setSlugManual(false);
    setSlugStatus("idle");
    setFieldErrors({});
    setServerError("");
  };
  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ── Loading screen ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Input class ─────────────────────────────────────────────────────────
  const inputCls = "w-full bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg py-3 px-4 text-base text-foreground placeholder:text-muted-foreground/50 transition-[border-color,box-shadow]";

  // ── Slug status indicator ────────────────────────────────────────────────
  const SlugIndicator = () => {
    if (slugStatus === "checking") return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
    if (slugStatus === "available") return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (slugStatus === "taken" || slugStatus === "invalid") return <XCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  const canSubmit = !isCreating
    && !!selectedPackage
    && (!needsConsent || startConsent)
    && !!formData.brideName.trim()
    && !!formData.groomName.trim()
    && isSlugValid(formData.slug)
    && slugStatus !== "taken"
    && slugStatus !== "checking"
    && slugStatus !== "invalid";

  // ── Modal ───────────────────────────────────────────────────────────────
  const modal = showModal && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div className="bg-card border border-border rounded-xl w-full max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto shadow-2xl">
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif text-2xl italic text-foreground">
              {t("wedding.createTitle")}
            </h2>
            <button
              onClick={closeModal}
              className="text-muted-foreground hover:text-primary transition-colors p-1"
              aria-label={t("common.cancel")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Bride & Groom */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("wedding.bride")}
                </label>
                <input
                  type="text"
                  placeholder="Ayşe"
                  maxLength={60}
                  value={formData.brideName}
                  onFocus={() => clearFieldError("brideName")}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({ ...prev, brideName: v }));
                    updateAutoSlug(v, formData.groomName);
                  }}
                  onBlur={(e) => {
                    const err = validateName(e.target.value) ?? (e.target.value ? undefined : t("wedding.enterBride"));
                    if (err) setFieldError("brideName", err);
                  }}
                  className={`${inputCls} ${fieldErrors.brideName ? "border-red-500/60" : ""}`}
                />
                {fieldErrors.brideName && <p className="text-xs text-red-500">{fieldErrors.brideName}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("wedding.groom")}
                </label>
                <input
                  type="text"
                  placeholder="Mehmet"
                  maxLength={60}
                  value={formData.groomName}
                  onFocus={() => clearFieldError("groomName")}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({ ...prev, groomName: v }));
                    updateAutoSlug(formData.brideName, v);
                  }}
                  onBlur={(e) => {
                    const err = validateName(e.target.value) ?? (e.target.value ? undefined : t("wedding.enterGroom"));
                    if (err) setFieldError("groomName", err);
                  }}
                  className={`${inputCls} ${fieldErrors.groomName ? "border-red-500/60" : ""}`}
                />
                {fieldErrors.groomName && <p className="text-xs text-red-500">{fieldErrors.groomName}</p>}
              </div>
            </div>

            {/* Slug */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  URL Slug
                </label>
                <span className="text-xs">
                  {slugStatus === "checking" && <span className="text-muted-foreground">{t("wedding.slugChecking")}</span>}
                  {slugStatus === "available" && <span className="text-green-500">{t("wedding.slugAvailable")}</span>}
                  {slugStatus === "taken" && <span className="text-red-500">{t("wedding.slugTaken")}</span>}
                  {slugStatus === "invalid" && <span className="text-red-500">{t("wedding.slugInvalidFormat")}</span>}
                </span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("wedding.slugPlaceholder")}
                  maxLength={48}
                  value={formData.slug}
                  onFocus={() => { if (formData.slug) void checkSlug(formData.slug); }}
                  onChange={(e) => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                    setSlugManual(true);
                    setFormData((prev) => ({ ...prev, slug: v }));
                    setSlugStatus("idle");
                  }}
                  className={`${inputCls} pr-10`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                  <SlugIndicator />
                </div>
              </div>
            </div>

            {/* Date & Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("wedding.datePlaceholder")}
                </label>
                <input
                  type="datetime-local"
                  value={formData.dateTime}
                  onChange={(e) => setFormData((prev) => ({ ...prev, dateTime: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("wedding.locationPlaceholder")}
                </label>
                <input
                  type="text"
                  placeholder={t("wedding.locationPlaceholder")}
                  maxLength={120}
                  value={formData.location}
                  onFocus={() => clearFieldError("location")}
                  onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                  onBlur={(e) => {
                    const err = validateLocation(e.target.value);
                    if (err) setFieldError("location", err);
                  }}
                  className={`${inputCls} ${fieldErrors.location ? "border-red-500/60" : ""}`}
                />
                {fieldErrors.location && <p className="text-xs text-red-500">{fieldErrors.location}</p>}
              </div>
            </div>

            <fieldset className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="create-package-choice">
              <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("wedding.packageForWedding")}</legend>
              {packages.available.map((p) => (
                <label key={p.id} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="use-package"
                    checked={usePackage === p.id}
                    onChange={() => {
                      setUsePackage(p.id);
                      setStartConsent(false);
                      setServerError("");
                    }}
                    className="mt-0.5 accent-primary cursor-pointer"
                  />
                  <span>
                    {pkgText(p)}
                    {p.number && <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">{p.number}</span>}
                  </span>
                </label>
              ))}
              {packages.pending.map((p) => (
                <label key={p.id} className="flex items-start gap-2 text-sm opacity-50 cursor-not-allowed">
                  <input type="radio" name="use-package" disabled className="mt-0.5 cursor-not-allowed" />
                  <span>{t("wedding.packagePendingOption", { pkg: pkgText(p), number: p.number })}</span>
                </label>
              ))}
              <p className="text-xs text-muted-foreground">{t("wedding.packageChoiceHint")}</p>
            </fieldset>

            {selectedPackage?.source === "free" && (
              <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground leading-relaxed" data-testid="create-trial-notice">
                {selectedPackage.id === "trial" && accountWide
                  ? t("wedding.createAccountWideNotice", { plan: accountWide.label, n: selectedPackage.limits.guests })
                  : t("wedding.createFreeNotice", { plan: selectedPackage.label, n: selectedPackage.limits.guests })}
              </p>
            )}

            {needsConsent && (
              <label className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-xs leading-relaxed cursor-pointer" data-testid="create-start-consent">
                <input
                  type="checkbox"
                  checked={startConsent}
                  onChange={(e) => setStartConsent(e.target.checked)}
                  className="mt-0.5 accent-primary cursor-pointer"
                />
                <span>{t("wedding.startConsent")}</span>
              </label>
            )}

            {serverError && <p className="text-sm text-red-500">{serverError}</p>}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleCreate}
                disabled={!canSubmit}
                className="flex-1 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                {isCreating ? t("common.creating") : t("common.create")}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 font-semibold py-3 rounded-lg transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Wedding card ────────────────────────────────────────────────────────
  const WeddingCard = ({ w }: { w: WeddingSummary }) => (
    <div className="bg-primary/5 border border-primary/20 rounded-xl overflow-hidden hover:border-primary/40 transition-all group">
      <div className="relative h-44 w-full">
        <MonogramPlaceholder title={w.title} />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
        <button
          onClick={(e) => { e.stopPropagation(); handleArchive(w.slug); }}
          className="absolute top-3 right-3 w-8 h-8 bg-background/70 backdrop-blur-sm border border-primary/20 rounded-lg flex items-center justify-center text-muted-foreground hover:text-amber-500 hover:border-amber-400/30 transition-colors opacity-0 group-hover:opacity-100"
          title={t("wedding.archiveButton")}
        >
          <Archive className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-foreground mb-2 leading-snug">{w.title}</h3>
        {w.package && (
          <p
            data-testid={`wedding-package-${w.slug}`}
            className={cn(
              "mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
              w.package.source === "order" ? "bg-primary/15 text-primary"
                : w.package.source === "suspended" ? "bg-red-500/10 text-red-700 dark:text-red-400"
                : "bg-muted text-muted-foreground",
            )}
          >
            <Package className="h-3 w-3" aria-hidden="true" />
            <span>{cardLabel(w.package)}</span>
            {w.package.orderNumber && <span className="tabular-nums opacity-70">{w.package.orderNumber}</span>}
          </p>
        )}
        <div className="space-y-1 mb-4">
          {w.date_time && (
            <div className="flex items-center gap-1.5 text-sm text-primary">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span>{new Date(w.date_time).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
          )}
          {w.location && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{w.location}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => navigate(paths.wedding.dashboard(w.slug))}
          className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 text-white font-semibold py-2.5 rounded-lg text-sm transition-[background-color,transform] flex items-center justify-center gap-2"
        >
          {t("wedding.manage")}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Kullanılabilir paket yoksa: "Paket al" (Faz 1: tanıtım sitesindeki paketler; Faz 2 uygulama içi).
  const buyLink = (
    <a
      href={`${WEB_BASE}/#plans`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="buy-package"
      className="inline-flex items-center gap-1.5 rounded-xl border border-primary/40 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors cursor-pointer"
    >
      <span>{t("wedding.buyPackage")}</span>
      <ExternalLink className="h-4 w-4" aria-hidden="true" />
    </a>
  );

  // ── Empty state ─────────────────────────────────────────────────────────
  const emptyState = (
    <div className="flex flex-col items-center justify-center py-24 text-center px-6">
      <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
        <Heart className="w-10 h-10 text-primary/40" />
      </div>
      <h2 className="font-serif text-2xl text-foreground mb-2">{t("wedding.noWeddings")}</h2>
      <p className="text-muted-foreground text-sm mb-8 max-w-xs">
        {canCreate ? t("wedding.noWeddingsHint") : t("wedding.noPackageHint")}
      </p>
      {canCreate ? (
        <button
          onClick={() => openCreate()}
          className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-xl flex items-center gap-2 transition-colors cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          {t("wedding.newWedding")}
        </button>
      ) : buyLink}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <OrganizerPageHeader />

      <main className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        {/* ── Desktop: page title + new wedding button ── */}
        {isDesktop && (
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-4xl italic text-foreground">{t("wedding.myWeddings")}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                {organizer?.displayName ?? organizer?.email}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!canCreate && buyLink}
              <button
                onClick={() => openCreate()}
                disabled={!canCreate}
                title={canCreate ? undefined : t("wedding.noPackageHint")}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-primary/20 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                {t("wedding.newWedding")}
              </button>
            </div>
          </div>
        )}

        {/* ── Paket cüzdanı: kullanılabilir / kullanımda / ödeme bekleyen (toplam sipariş DEĞİL) ── */}
        {isDesktop ? (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
              <p className="text-muted-foreground text-sm font-medium mb-1">{t("wedding.myWeddings")}</p>
              <p className="text-3xl font-bold text-foreground">{totalWeddings}</p>
            </div>
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6" data-testid="package-wallet">
              <p className="text-muted-foreground text-sm font-medium mb-1">{t("wedding.walletAvailable")}</p>
              <p className="text-2xl font-bold text-foreground">{walletList || t("wedding.walletNone")}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{t("wedding.walletInUse", { n: packages.inUse.length })}</span>
                {packages.pending.length > 0 && <span>{t("wedding.walletPending", { n: packages.pending.length })}</span>}
                <AppNavLink to={paths.packages} className="font-semibold text-primary hover:underline cursor-pointer">
                  {t("wedding.walletLink")}
                </AppNavLink>
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground" data-testid="package-wallet-mobile">
            <span>
              {t("wedding.walletAvailable")}: <span className="font-semibold text-foreground">{walletList || t("wedding.walletNone")}</span>
            </span>
            <AppNavLink to={paths.packages} className="text-xs font-semibold text-primary hover:underline cursor-pointer">
              {t("wedding.walletLink")}
            </AppNavLink>
            {!canCreate && buyLink}
          </div>
        )}

        {/* ── Kullanılabilir paketi mevcut düğüne tanımla: ücretli → Free/geçiş paketli düğünü yükseltir ── */}
        {assignable.length > 0 && (
          <div className="mb-6 space-y-3 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid="pending-packages" role="region" aria-label={t("wedding.upgradeTitle")}>
            <p className="flex items-center gap-2 font-semibold text-foreground">
              <Package className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{t("wedding.upgradeTitle")}</span>
            </p>
            {assignable.map((p) => {
              const paid = p.source === "order";
              return (
                <div key={p.id} className="space-y-2 rounded-xl bg-background/60 p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <p className="min-w-0 flex-1 text-sm">
                      <span className="font-medium">{pkgText(p)}</span>
                      {p.number && <span className="ml-2 text-xs text-muted-foreground tabular-nums">{p.number}</span>}
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select
                        value={applyTarget[p.id] ?? ""}
                        onChange={(e) => setApplyTarget((prev) => ({ ...prev, [p.id]: e.target.value }))}
                        aria-label={t("wedding.applyTo")}
                        className="min-w-0 rounded-lg border border-border bg-background px-2 py-2 text-sm cursor-pointer sm:w-56 sm:py-1.5"
                      >
                        <option value="">{t("wedding.applyTo")}</option>
                        {assignTargets(p).map((w) => (
                          <option key={w.slug} value={w.slug}>{w.title}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => void handleApply(p)}
                        disabled={!applyTarget[p.id] || (paid && !applyConsent[p.id]) || applying !== null}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer sm:py-1.5"
                      >
                        {applying === p.id && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                        <span>{t("wedding.applyButton")}</span>
                      </button>
                    </div>
                  </div>
                  {paid && applyTarget[p.id] && (
                    <label className="flex items-start gap-2 text-xs leading-relaxed cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!applyConsent[p.id]}
                        onChange={(e) => setApplyConsent((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                        className="mt-0.5 accent-primary cursor-pointer"
                      />
                      <span>{t("wedding.startConsent")}</span>
                    </label>
                  )}
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground">{t("wedding.upgradeHint")}</p>
          </div>
        )}

        {/* ── Wedding list or empty state ── */}
        {weddings.length === 0 ? emptyState : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {weddings.map((w) => <WeddingCard key={w.slug} w={w} />)}
          </div>
        )}

        {/* ── Archived weddings ── */}
        {archivedWeddings.length > 0 && (
          <div className="mt-10">
            <button
              onClick={() => setShowArchived(v => !v)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer mb-4"
            >
              <Archive className="w-4 h-4" />
              {t("wedding.archivedSection")} ({archivedWeddings.length})
              <ChevronDown className={cn("w-4 h-4 transition-transform", showArchived && "rotate-180")} />
            </button>
            {showArchived && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {archivedWeddings.map(w => (
                  <div key={w.slug} className="relative bg-primary/5 border border-primary/20 rounded-xl overflow-hidden">
                    {/* Card content (same as active) */}
                    <div className="relative h-44 w-full">
                      <MonogramPlaceholder title={w.title} />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-xl text-foreground mb-2 leading-snug">{w.title}</h3>
                      <div className="space-y-1 mb-4">
                        {w.date_time && (
                          <div className="flex items-center gap-1.5 text-sm text-primary">
                            <Calendar className="w-3.5 h-3.5 shrink-0" />
                            <span>{new Date(w.date_time).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" })}</span>
                          </div>
                        )}
                        {w.location && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">{w.location}</span>
                          </div>
                        )}
                      </div>
                      {/* placeholder so card height matches active card */}
                      <div className="h-10" />
                    </div>
                    {/* Blur overlay + restore button */}
                    <div className="absolute inset-0 backdrop-blur-[1.5px] bg-background/20 flex flex-col items-center justify-center gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-background/80 border border-border px-3 py-1 text-xs font-semibold text-muted-foreground">
                        <Archive className="w-3.5 h-3.5" />
                        {t("wedding.archivedBadge")}
                      </span>
                      <button
                        onClick={() => handleUnarchive(w.slug)}
                        className="inline-flex items-center gap-2 bg-card border border-border rounded-xl px-5 py-2.5 text-sm font-semibold text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer shadow-sm"
                      >
                        <ArchiveRestore className="w-4 h-4" />
                        {t("wedding.unarchiveButton")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Mobile FAB ── */}
      {!isDesktop && canCreate && (
        <div className="fixed bottom-6 right-6 z-20">
          <button
            onClick={() => openCreate()}
            className="bg-primary hover:bg-primary/90 active:scale-95 motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-lg shadow-primary/30 flex items-center gap-2 transition-[background-color,transform]"
          >
            <Plus className="w-4 h-4" />
            {t("wedding.newWedding")}
          </button>
        </div>
      )}

      {modal}

      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg pointer-events-none ${toast.ok ? "bg-green-600" : "bg-red-600"}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
